import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PostCard from './PostCard'
import EditProfileModal from './EditProfileModal'
import Lottie from './Lottie'
import './UserProfile.css'

function UserProfile({ userId, onBack, onUserClick }) {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('posts') // 'posts', 'friends', 'likes'

  // Persister le userId affiché dans localStorage (persiste après rechargement / nouvel onglet)
  useEffect(() => {
    try {
      if (userId) {
        localStorage.setItem('social_lastViewedUserId', userId)
      }
    } catch (_) {}
  }, [userId])

  // Écouter l'événement de token expiré pour préserver userId si possible
  useEffect(() => {
    const handleTokenExpired = () => {
      try {
        if (userId) {
          localStorage.setItem('social_lastViewedUserId', userId)
        }
      } catch (_) {}
    }
    window.addEventListener('auth:token-expired', handleTokenExpired)
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired)
  }, [userId])

  // Si userId est null (ex. après rechargement), utiliser le dernier userId vu (localStorage)
  const effectiveUserId = userId || (typeof localStorage !== 'undefined' ? localStorage.getItem('social_lastViewedUserId') : null)

  // Debug: vérifier userId
  console.log('[UserProfile] userId:', userId, 'effectiveUserId:', effectiveUserId, 'currentUser?.id:', currentUser?.id)

  // Récupérer le profil utilisateur
  const { data: profileData, error: profileError, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['social-user-profile', effectiveUserId],
    queryFn: () => socialService.getUserProfile(effectiveUserId),
    enabled: !!effectiveUserId,
    retry: false
  })

  // Récupérer les posts de l'utilisateur
  const { data: postsData, refetch: refetchPosts, error: postsError, isLoading: isLoadingPosts } = useQuery({
    queryKey: ['social-user-posts', effectiveUserId],
    queryFn: () => socialService.getUserPosts(effectiveUserId),
    enabled: !!effectiveUserId && activeTab === 'posts',
    retry: false
  })

  // Debug: vérifier les données
  console.log('[UserProfile] profileData:', profileData, 'postsData:', postsData)
  console.log('[UserProfile] isLoadingProfile:', isLoadingProfile, 'isLoadingPosts:', isLoadingPosts)
  console.log('[UserProfile] profileError:', profileError, 'postsError:', postsError)

  // Calculer isOwnProfile avant de l'utiliser dans les requêtes
  const isOwnProfile = effectiveUserId === currentUser?.id

  // Récupérer les amis de l'utilisateur
  const { data: friendsData, error: friendsError } = useQuery({
    queryKey: ['social-user-friends', effectiveUserId],
    queryFn: () => socialService.getUserFriends(effectiveUserId),
    enabled: !!effectiveUserId && activeTab === 'friends',
    retry: false
  })

  // Récupérer les posts likés par l'utilisateur
  const { data: likedPostsData, error: likedPostsError, isLoading: isLoadingLikedPosts } = useQuery({
    queryKey: ['social-user-liked-posts', effectiveUserId],
    queryFn: () => socialService.getUserLikedPosts(effectiveUserId),
    enabled: !!effectiveUserId && activeTab === 'likes' && isOwnProfile,
    retry: false
  })

  const profile = profileData?.user || {}
  const posts = postsData?.posts || []
  const friends = friendsData?.friends || []
  const likedPosts = likedPostsData?.posts || []
  const isFriend = profileData?.is_friend || false
  
  // Debug: vérifier les données du profil
  useEffect(() => {
    console.log('[UserProfile] profileData:', profileData)
    console.log('[UserProfile] profile:', profile)
    console.log('[UserProfile] is_friend:', profileData?.is_friend, 'isFriend:', isFriend)
  }, [profileData, profile, isFriend])
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  // Mutation pour envoyer une demande d'ami
  const sendRequestMutation = useMutation({
    mutationFn: () => socialService.sendFriendRequest(effectiveUserId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-user-profile', effectiveUserId])
      // Afficher l'animation de succès
      setShowSuccessMessage(true)
      setTimeout(() => {
        if (successLottieRef.current && successAnimation) {
          successLottieRef.current.goToAndPlay(0)
        }
      }, 100)
      // Masquer après 3 secondes
      setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)
    },
    onError: (error) => {
      // Vérifier si c'est une erreur de demande déjà envoyée
      const errorMessage = error?.response?.data?.detail || error?.message || ''
      if (errorMessage.includes('existe déjà') || errorMessage.includes('déjà')) {
        // Afficher le message avec l'animation refuse
        setShowPendingMessage(true)
        setTimeout(() => {
          if (refuseLottieRef.current && refuseAnimation) {
            refuseLottieRef.current.goToAndPlay(0)
          }
        }, 100)
        // Masquer après 3 secondes
        setTimeout(() => {
          setShowPendingMessage(false)
        }, 3000)
      } else {
        // Autre erreur, afficher un alert
        alert(errorMessage || 'Erreur lors de l\'envoi de la demande d\'ami')
      }
    }
  })

  // Mutation pour follow/unfollow
  const followMutation = useMutation({
    mutationFn: () => socialService.followUser(effectiveUserId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-user-profile', effectiveUserId])
    },
    onError: (error) => {
      console.error('[UserProfile] Erreur lors de l\'abonnement:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur lors de l\'abonnement'
      alert(errorMessage)
    }
  })

  const unfollowMutation = useMutation({
    mutationFn: () => socialService.unfollowUser(effectiveUserId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-user-profile', effectiveUserId])
    },
    onError: (error) => {
      console.error('[UserProfile] Erreur lors du désabonnement:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur lors du désabonnement'
      alert(errorMessage)
    }
  })

  // Mutation pour supprimer un ami
  const removeFriendMutation = useMutation({
    mutationFn: () => socialService.removeFriend(effectiveUserId),
    onSuccess: () => {
      queryClient.invalidateQueries(['social-user-profile', effectiveUserId])
      queryClient.invalidateQueries(['social-friends'])
      // Invalider aussi le profil de l'utilisateur connecté si on supprime un ami de son propre profil
      if (isOwnProfile) {
        queryClient.invalidateQueries(['social-user-profile', currentUser?.id])
      }
    },
    onError: (error) => {
      console.error('[UserProfile] Erreur lors de la suppression:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur lors de la suppression'
      alert(errorMessage)
    }
  })

  const [deleteAnimation, setDeleteAnimation] = useState(null)
  const deleteLottieRef = useRef(null)
  const [successAnimation, setSuccessAnimation] = useState(null)
  const successLottieRef = useRef(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [refuseAnimation, setRefuseAnimation] = useState(null)
  const refuseLottieRef = useRef(null)
  const [showPendingMessage, setShowPendingMessage] = useState(false)

  useEffect(() => {
    fetch('/Delete_animation.json')
      .then(res => res.json())
      .then(data => setDeleteAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Delete_animation.json:', err))
    
    fetch('/Succes.json')
      .then(res => res.json())
      .then(data => setSuccessAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Succes.json:', err))
    
    fetch('/refuse.json')
      .then(res => res.json())
      .then(data => setRefuseAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de refuse.json:', err))
  }, [])

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  const handleRemoveFriend = () => {
    setShowRemoveConfirm(true)
  }

  const confirmRemoveFriend = () => {
    if (deleteLottieRef.current) {
      deleteLottieRef.current.goToAndPlay(0)
    }
    removeFriendMutation.mutate()
    setShowRemoveConfirm(false)
  }

  const cancelRemoveFriend = () => {
    setShowRemoveConfirm(false)
  }

  const handleSendFriendRequest = () => {
    sendRequestMutation.mutate()
  }

  const handleFollow = () => {
    if (profileData?.is_following) {
      unfollowMutation.mutate()
    } else {
      followMutation.mutate()
    }
  }

  if (!effectiveUserId) {
    return (
      <div className="user-profile">
        <div className="empty-state">
          {!currentUser ? (
            <>
              <h3>Session absente</h3>
              <p>Reconnectez-vous pour afficher un profil.</p>
            </>
          ) : (
            <>
              <h3>Profil indisponible</h3>
              <p>L’identifiant utilisateur n’a pas pu être déterminé. Réouvrez le profil depuis la navigation.</p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="user-profile">
      {isLoadingProfile ? (
        <div className="empty-state">
          <div className="loader"></div>
        </div>
      ) : profileError?.response?.status === 401 ? (
        <div className="empty-state">
          <h3 style={{ color: '#ff6b6b' }}>Session expirée</h3>
          <p>Votre session a expiré. Veuillez vous reconnecter pour accéder au profil.</p>
          <button 
            onClick={() => window.location.href = '/login'}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: 'rgba(74, 158, 255, 0.2)',
              border: '1px solid rgba(74, 158, 255, 0.5)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
          >
            Se reconnecter
          </button>
        </div>
      ) : profileError?.response?.status === 405 ? (
        <div className="empty-state">
          <h3 style={{ color: '#ff6b6b' }}>⚠️ Endpoint non disponible</h3>
          <p>Le profil utilisateur n'est pas encore disponible côté backend.</p>
        </div>
      ) : (
        <div className="profile-header">
          <div 
            className="profile-avatar"
            style={{
              backgroundImage: profile.avatar_url ? `url(${profile.avatar_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!profile.avatar_url && (profile.email?.charAt(0).toUpperCase() || 'U')}
          </div>
          <div className="profile-info">
            <div className="profile-name-row">
              <div className="profile-name-section">
                <h2>{profile.display_name || (profile.username ? `@${profile.username}` : 'Utilisateur')}</h2>
                {profile.username && (
                  <span className="profile-username">@{profile.username}</span>
                )}
              </div>
              <div className="profile-actions">
                {isOwnProfile ? (
              <button
                className="edit-profile-btn Btn"
                onClick={() => setIsEditModalOpen(true)}
              >
                Modifier le profil
                <svg viewBox="0 0 512 512" className="svg">
                  <path
                    d="M410.3 231l11.3-11.3-33.9-33.9-62.1-62.1L291.7 89.8l-11.3 11.3-22.6 22.6L58.6 322.9c-10.4 10.4-18 23.3-22.2 37.4L1 480.7c-2.5 8.4-.2 17.5 6.1 23.7s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L387.7 253.7 410.3 231zM160 399.4l-9.1 22.7c-4 3.1-8.5 5.4-13.3 6.9L59.4 452l23-78.1c1.4-4.9 3.8-9.4 6.9-13.3l22.7-9.1v32c0 8.8 7.2 16 16 16h32zM362.7 18.7L348.3 33.2 325.7 55.8 314.3 67.1l33.9 33.9 62.1 62.1 33.9 33.9 11.3-11.3 22.6-22.6 14.5-14.5c25-25 25-65.5 0-90.5L453.3 18.7c-25-25-65.5-25-90.5 0zm-47.4 168l-144 144c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l144-144c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z"
                  ></path>
                </svg>
              </button>
                ) : (
                  <>
                    <button
                      className={`follow-btn ${profileData?.is_following ? 'following' : ''}`}
                  onClick={handleFollow}
                  disabled={followMutation.isLoading || unfollowMutation.isLoading}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                  </svg>
                  {profileData?.is_following ? 'Abonné' : 'S\'abonner'}
                </button>
                {isFriend ? (
                  <div
                    className="plusButton remove-friend-btn"
                    onClick={() => !removeFriendMutation.isLoading && handleRemoveFriend()}
                    style={{ 
                      cursor: removeFriendMutation.isLoading ? 'not-allowed' : 'pointer', 
                      opacity: removeFriendMutation.isLoading ? 0.5 : 1 
                    }}
                    title="Supprimer l'ami"
                    tabIndex={0}
                  >
                    {deleteAnimation ? (
                      <Lottie
                        lottieRef={deleteLottieRef}
                        animationData={deleteAnimation}
                        loop={false}
                        autoplay={false}
                        style={{ width: '70%', height: '70%', pointerEvents: 'none' }}
                      />
                    ) : (
                      <span style={{ pointerEvents: 'none', fontSize: '1.2em' }}>✕</span>
                    )}
                  </div>
                ) : (
                  <div
                    className="plusButton friend-request-btn"
                    onClick={() => !sendRequestMutation.isLoading && handleSendFriendRequest()}
                    style={{ 
                      cursor: sendRequestMutation.isLoading ? 'not-allowed' : 'pointer', 
                      opacity: sendRequestMutation.isLoading ? 0.5 : 1 
                    }}
                    tabIndex={0}
                  >
                    <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                      <g mask="url(#mask0_21_345)">
                        <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                      </g>
                    </svg>
                  </div>
                )}
              </>
            )}
              </div>
            </div>
            {profile.description && (
              <p className="profile-description">{profile.description}</p>
            )}
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-value">{posts.length}</span>
                <span className="stat-label">Posts</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{profileData?.followers_count || 0}</span>
                <span className="stat-label">Abonnés</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{profileData?.following_count || 0}</span>
                <span className="stat-label">Abonnements</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{profileData?.friends_count ?? friends.length}</span>
                <span className="stat-label">Amis</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <EditProfileModal
          user={profile}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}

      {/* Animation de succès pour demande d'ami */}
      {showSuccessMessage && (
        <div className="success-message-overlay" onClick={() => setShowSuccessMessage(false)}>
          <div className="success-message-modal" onClick={(e) => e.stopPropagation()}>
            {successAnimation && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Lottie
                  lottieRef={successLottieRef}
                  animationData={successAnimation}
                  loop={false}
                  autoplay={true}
                  style={{ width: 120, height: 120, margin: '0 auto' }}
                />
              </div>
            )}
            <h3>Demande d'ami envoyée avec succès</h3>
          </div>
        </div>
      )}

      {/* Message pour demande déjà envoyée */}
      {showPendingMessage && (
        <div className="success-message-overlay" onClick={() => setShowPendingMessage(false)}>
          <div className="success-message-modal" onClick={(e) => e.stopPropagation()}>
            {refuseAnimation && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Lottie
                  lottieRef={refuseLottieRef}
                  animationData={refuseAnimation}
                  loop={false}
                  autoplay={true}
                  style={{ width: 120, height: 120, margin: '0 auto' }}
                />
              </div>
            )}
            <h3>Vous avez déjà envoyé une demande d'ami</h3>
            <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.9em', textAlign: 'center' }}>
              La demande est en attente de réponse
            </p>
          </div>
        </div>
      )}

      <div className="profile-tabs">
        <button
          className={`tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          Posts
        </button>
        <button
          className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          Amis
        </button>
        {isOwnProfile && (
          <button
            className={`tab-btn ${activeTab === 'likes' ? 'active' : ''}`}
            onClick={() => setActiveTab('likes')}
          >
            Likes
          </button>
        )}
      </div>

      <div className="profile-content">
        {activeTab === 'posts' && (
          <div className="posts-section">
            {isLoadingPosts ? (
              <div className="empty-state">
                <div className="loader"></div>
              </div>
            ) : postsError?.response?.status === 405 ? (
              <div className="empty-state">
                <p style={{ color: '#ff6b6b' }}>⚠️ Endpoint non disponible</p>
              </div>
            ) : postsError ? (
              <div className="empty-state">
                <p style={{ color: '#ff6b6b' }}>Erreur: {postsError.message || 'Erreur lors du chargement des posts'}</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="empty-state">
                <p>Aucun post pour le moment</p>
              </div>
            ) : (
              <div className="posts-list">
                {posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onUpdate={refetchPosts}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'likes' && isOwnProfile && (
          <div className="liked-posts-section">
            {isLoadingLikedPosts ? (
              <div className="empty-state">
                <div className="loader"></div>
                <p>Chargement...</p>
              </div>
            ) : likedPostsError?.response?.status === 405 ? (
              <div className="empty-state">
                <p style={{ color: '#ff6b6b' }}>⚠️ Endpoint non disponible</p>
              </div>
            ) : likedPostsError ? (
              <div className="empty-state">
                <p style={{ color: '#ff6b6b' }}>Erreur: {likedPostsError.message || 'Erreur lors du chargement des posts likés'}</p>
              </div>
            ) : likedPosts.length === 0 ? (
              <div className="empty-state">
                <p>Aucun post liké pour le moment</p>
              </div>
            ) : (
              <div className="posts-list">
                {likedPosts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onUpdate={refetchPosts}
                    onUserClick={onUserClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'friends' && (
          <div className="friends-section">
            {friendsError?.response?.status === 405 ? (
              <div className="empty-state">
                <p style={{ color: '#ff6b6b' }}>⚠️ Endpoint non disponible</p>
              </div>
            ) : friends.length === 0 ? (
              <div className="empty-state">
                <p>Aucun ami pour le moment</p>
              </div>
            ) : (
              <div className="friends-grid">
                {friends.map(friend => (
                  <div 
                    key={friend.id} 
                    className="friend-card"
                    onClick={() => onUserClick && onUserClick(friend.id)}
                    style={{ cursor: onUserClick ? 'pointer' : 'default' }}
                  >
                    <div className="friend-avatar">
                      {friend.avatar_url ? (
                        <img src={friend.avatar_url} alt="" />
                      ) : (
                        (friend.display_name?.charAt(0) || 
                         friend.username?.charAt(0) || 
                         friend.email?.charAt(0) || 
                         'U').toUpperCase()
                      )}
                    </div>
                    <div className="friend-info">
                      {friend.display_name && (
                        <div className="friend-name">{friend.display_name}</div>
                      )}
                      {friend.username && (
                        <div className="friend-username">@{friend.username}</div>
                      )}
                      {!friend.display_name && !friend.username && (
                        <div className="friend-name">{friend.email || 'Utilisateur'}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      {showRemoveConfirm && (
        <RemoveFriendConfirmDialog
          friend={profile}
          onConfirm={confirmRemoveFriend}
          onCancel={cancelRemoveFriend}
        />
      )}
    </div>
  )
}

// Composant de confirmation de suppression
function RemoveFriendConfirmDialog({ friend, onConfirm, onCancel }) {
  const acceptLottieRef = useRef(null)
  const denyLottieRef = useRef(null)
  const [acceptAnimation, setAcceptAnimation] = useState(null)
  const [denyAnimation, setDenyAnimation] = useState(null)

  useEffect(() => {
    // Charger Accept.json
    fetch('/Accept.json')
      .then(res => res.json())
      .then(data => setAcceptAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Accept.json:', err))
    
    // Charger Denyv2.json - exactement comme Accept.json
    fetch('/Denyv2.json')
      .then(res => res.json())
      .then(data => setDenyAnimation(data))
      .catch(err => console.error('Erreur lors du chargement de Denyv2.json:', err))
  }, [])

  const handleConfirm = () => {
    if (acceptLottieRef.current) {
      acceptLottieRef.current.goToAndPlay(0)
    }
    setTimeout(() => onConfirm(), 300)
  }

  const handleCancel = () => {
    if (denyLottieRef.current) {
      denyLottieRef.current.goToAndPlay(0)
    }
    setTimeout(() => onCancel(), 300)
  }

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div className="confirm-dialog-motion" onClick={(e) => e.stopPropagation()}>
      <div className="confirm-dialog glass-card">
        <div className="confirm-dialog-header">
          <div 
            className="confirm-user-avatar"
            style={{
              backgroundImage: friend?.avatar_url ? `url(${friend.avatar_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!friend?.avatar_url && (
              (friend?.display_name?.charAt(0) || 
               friend?.username?.charAt(0) || 
               friend?.email?.charAt(0) || 
               'U').toUpperCase()
            )}
          </div>
          <h3>Supprimer cet ami ?</h3>
          <p>
            {friend?.display_name || 
             (friend?.username ? `@${friend.username}` : null) || 
             friend?.email || 
             'Utilisateur'}
          </p>
        </div>
        <div className="confirm-dialog-actions">
          <div
            className="confirm-btn-lottie confirm-btn"
            onClick={handleConfirm}
            style={{ 
              minWidth: '50px', 
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {acceptAnimation ? (
              <Lottie
                lottieRef={acceptLottieRef}
                animationData={acceptAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 40, height: 40 }}
              />
            ) : (
              <span style={{ 
                fontSize: '32px', 
                color: '#51cf66',
                display: 'inline-block',
                lineHeight: '1',
                fontWeight: 'bold'
              }}>✓</span>
            )}
          </div>
          <div
            className="confirm-btn-lottie cancel-btn"
            onClick={handleCancel}
            style={{ 
              minWidth: '50px', 
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 10
            }}
          >
            {denyAnimation ? (
              <Lottie
                lottieRef={denyLottieRef}
                animationData={denyAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 40, height: 40, position: 'relative', zIndex: 11 }}
              />
            ) : (
              <span style={{ 
                fontSize: '32px', 
                color: '#ff6b6b',
                display: 'inline-block',
                lineHeight: '1',
                fontWeight: 'bold',
                position: 'relative',
                zIndex: 11
              }}>✕</span>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export default UserProfile

